import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/admin/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { 
  Calendar, Users, CreditCard, TrendingUp, 
  Clock, AlertCircle, Loader2, RefreshCw, Bell, BellOff, BellRing
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import usePushNotifications from '../../hooks/usePushNotifications';

const API_BASE = window.location.origin || '';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [upcomingPickups, setUpcomingPickups] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Push notifications hook
  const { 
    isSupported: notificationsSupported, 
    permission: notificationPermission, 
    isLoading: notificationLoading,
    isRegistered: notificationRegistered,
    enableNotifications,
    sendTestNotification
  } = usePushNotifications();

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const headers = { ...getAuthHeader(), 'Content-Type': 'application/json' };

      const [statsRes, bookingsRes, pickupsRes, alertsRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/dashboard/stats`, { headers }),
        fetch(`${API_BASE}/api/admin/dashboard/recent-bookings?limit=10`, { headers }),
        fetch(`${API_BASE}/api/admin/dashboard/upcoming-pickups?limit=8`, { headers }),
        fetch(`${API_BASE}/api/admin/dashboard/alerts`, { headers }),
      ]);

      if (statsRes.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('vipgez_admin_token');
        navigate('/login-goturkeytransfer');
        return;
      }

      const [statsData, bookingsData, pickupsData, alertsData] = await Promise.all([
        statsRes.json(),
        bookingsRes.json(),
        pickupsRes.json(),
        alertsRes.json(),
      ]);

      if (statsData.success) setStats(statsData.stats);
      if (bookingsData.success) setRecentBookings(bookingsData.bookings || []);
      if (pickupsData.success) setUpcomingPickups(pickupsData.pickups || []);
      if (alertsData.success) setAlerts(alertsData.alerts || []);
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token');
    if (!token) {
      navigate('/login-goturkeytransfer');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US');
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      confirmed: 'bg-green-500/20 text-green-400',
      confirmed_cash: 'bg-yellow-500/20 text-yellow-400',
      pending: 'bg-blue-500/20 text-blue-400',
      cancelled: 'bg-red-500/20 text-red-400',
      completed: 'bg-gray-500/20 text-gray-400',
    };
    return statusColors[status] || 'bg-gray-500/20 text-gray-400';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex admin-theme">
        <AdminSidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex admin-theme">
      <AdminSidebar />
      
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground" data-testid="dashboard-title">Dashboard</h1>
              <p className="text-muted-foreground">GoTurkeyTransfer Admin Panel</p>
            </div>
            <div className="flex items-center gap-2">
              {/* Push Notification Button */}
              {notificationsSupported && (
                <div className="flex items-center gap-2">
                  {notificationPermission === 'granted' && notificationRegistered ? (
                    <Button 
                      onClick={sendTestNotification} 
                      variant="outline" 
                      size="sm"
                      data-testid="test-notification-btn"
                      className="text-green-500 border-green-500/30 hover:bg-green-500/10"
                    >
                      <BellRing className="h-4 w-4 mr-2" />
                      Test Bildirim
                    </Button>
                  ) : (
                    <Button 
                      onClick={enableNotifications} 
                      variant="outline" 
                      size="sm"
                      disabled={notificationLoading}
                      data-testid="enable-notifications-btn"
                      className="text-primary border-primary/30 hover:bg-primary/10"
                    >
                      {notificationLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Bell className="h-4 w-4 mr-2" />
                      )}
                      Bildirimleri Aç
                    </Button>
                  )}
                </div>
              )}
              <Button onClick={fetchData} variant="outline" size="sm" data-testid="refresh-btn">
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Alerts */}
          {alerts.length > 0 && (
            <div className="mb-6 space-y-2">
              {alerts.map((alert, i) => (
                <div
                  key={i}
                  className={`p-4 rounded-lg border flex items-start gap-3 ${
                    alert.type === 'warning' ? 'bg-yellow-500/10 border-yellow-500/30' :
                    alert.type === 'error' ? 'bg-red-500/10 border-red-500/30' :
                    'bg-blue-500/10 border-blue-500/30'
                  }`}
                >
                  <AlertCircle className="h-5 w-5 text-primary flex-shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">{alert.title}</p>
                    <p className="text-sm text-muted-foreground">{alert.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="bg-secondary border-border bg-secondary border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Calendar className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Today&apos;s Bookings</p>
                    <p className="text-2xl font-bold text-foreground">{stats?.todayBookings || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-secondary border-border bg-secondary border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-500/10 rounded-lg">
                    <Clock className="h-6 w-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Next 24 Hours</p>
                    <p className="text-2xl font-bold text-foreground">{stats?.upcoming24h || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-secondary border-border bg-secondary border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-500/10 rounded-lg">
                    <CreditCard className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Monthly Revenue</p>
                    <p className="text-2xl font-bold text-foreground">
                      {formatCurrency(stats?.paidRevenueMonth)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-secondary border-border bg-secondary border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-500/10 rounded-lg">
                    <Users className="h-6 w-6 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Customers</p>
                    <p className="text-2xl font-bold text-foreground">{stats?.totalCustomers || 0}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Two Column Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upcoming Pickups */}
            <Card className="bg-secondary border-border bg-secondary border-border">
              <CardHeader>
                <CardTitle className="text-lg text-foreground">Upcoming Transfers</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {upcomingPickups.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No upcoming transfers</p>
                  ) : (
                    upcomingPickups.map((pickup, i) => (
                      <div
                        key={i}
                        className={`p-3 rounded-lg bg-muted/30 border border-border ${
                          pickup.isUrgent ? 'border-l-4 border-l-yellow-500' : ''
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-foreground">{pickup.booking_ref}</span>
                          <span className={`text-xs px-2 py-1 rounded ${getStatusBadge(pickup.status)}`}>
                            {pickup.status}
                          </span>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          <p>{pickup.pickup_location?.substring(0, 30)}...</p>
                          <p className="text-primary">
                            {pickup.trip_date} {pickup.trip_time}
                            {pickup.isUrgent && <span className="ml-2 text-yellow-500">⚡ {pickup.hoursUntil}h</span>}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Bookings */}
            <Card className="bg-secondary border-border bg-secondary border-border">
              <CardHeader>
                <CardTitle className="text-lg text-foreground">Recent Bookings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recentBookings.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">No bookings found</p>
                  ) : (
                    recentBookings.slice(0, 8).map((booking, i) => (
                      <div key={i} className="p-3 rounded-lg bg-muted/30 border border-border">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-foreground">{booking.booking_ref}</span>
                          <span className="text-primary font-semibold">
                            {formatCurrency(booking.total)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between text-sm text-muted-foreground">
                          <span>{booking.customer_name || booking.customer_email?.split('@')[0]}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${getStatusBadge(booking.status)}`}>
                            {booking.status}
                          </span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary Stats */}
          <Card className="bg-secondary border-border bg-secondary border-border mt-6">
            <CardHeader>
              <CardTitle className="text-lg text-foreground flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-primary" />
                Summary Statistics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-bold text-foreground">{stats?.totalBookings || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Bookings</p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-bold text-foreground">{formatCurrency(stats?.totalRevenue)}</p>
                  <p className="text-sm text-muted-foreground">Total Revenue</p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-bold text-foreground">{stats?.monthBookings || 0}</p>
                  <p className="text-sm text-muted-foreground">This Month</p>
                </div>
                <div className="text-center p-4 bg-muted/30 rounded-lg">
                  <p className="text-2xl font-bold text-foreground">{stats?.pendingPayments?.count || 0}</p>
                  <p className="text-sm text-muted-foreground">Pending Payments</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
